/***
 * Copyright (c) Myia 2023-2025 - All Rights Reserved
 */
/* eslint-disable max-len */
import { animated, SpringValues, useSpring } from '@react-spring/web';
import { CSSProperties, FunctionComponent, ReactNode, useEffect, useRef } from 'react';

import useTabControls from './hooks/useTabControls';

interface TabItemProps {
    title: string;
    id?: string;
    content: ReactNode;
    tabHeader: ReactNode;
}

interface TabsProps {
    items: Array<TabItemProps | null>;
    initialIndex?: number;
    onChange?: (index: number) => void;
    onTabClick?: (idTab: string | undefined) => void;
    slot?: ReactNode;
}

export const Tabs: FunctionComponent<TabsProps> = ({ items = [], initialIndex = 0, onChange, onTabClick, slot }) => {
    const spacerRef = useRef<HTMLDivElement>(null);
    const [activeItem, setActiveItem, refsArray] = useTabControls(
        items.filter((item) => item !== null),
        initialIndex,
        onChange,
    );

    useEffect(() => {
        if (activeItem > items.length - 1) {
            setActiveItem(items.length - 1);
        }
    }, [items, activeItem, setActiveItem]);

    const scrollArea = useRef<HTMLDivElement>(null);
    const [{ scrollLeft }, scrollApi] = useSpring(() => ({
        from: { scrollLeft: 0 },
    }));

    const [underlineStyle, underlineApi] = useSpring(() => ({
        from: { left: 0, width: 0 },
        config: { tension: 300, friction: 30 },
    }));

    useEffect(() => {
        if (!refsArray.current || !scrollArea.current) {
            return;
        }

        const activeTab = refsArray.current[activeItem];
        if (activeTab) {
            const rect = activeTab.getBoundingClientRect();
            const scrollAreaRect = scrollArea.current.getBoundingClientRect();

            underlineApi.start({
                left: rect.left - scrollAreaRect.left + scrollArea.current.scrollLeft,
                width: rect.width,
            });
        }

        const spacerWidth = spacerRef?.current?.offsetWidth || 0;
        const rect = refsArray.current[activeItem].getBoundingClientRect();
        const scrollAreaScrollLeft = scrollArea.current?.scrollLeft;
        scrollApi.start({
            to: {
                scrollLeft: scrollAreaScrollLeft + rect.left - spacerWidth,
            },
        });
    }, [activeItem, refsArray, scrollApi, underlineApi]);

    return (
        <div className="relative z-0 h-full flex flex-col gap-y-[8px]" data-name="Tabs">
            <animated.div
                className="scrollbar-hide relative shrink-0 flex overflow-x-scroll"
                scrollLeft={scrollLeft}
                ref={scrollArea}
                style={
                    {
                        '--activeIndex': activeItem,
                    } as CSSProperties
                }
            >
                {items
                    .filter((item) => item !== null)
                    .map((item, idx) => (
                        <button
                            key={idx}
                            ref={(element) => {
                                /* @ts-ignore */
                                return refsArray.current && (refsArray.current[idx] = element);
                            }}
                            className="outline-0"
                            onClick={() => {
                                refsArray.current && refsArray.current[idx]?.focus();
                                setActiveItem(idx);
                                onTabClick?.(item.id);
                            }}
                        >
                            {item.tabHeader}
                        </button>
                    ))}
                {slot}
                <div className="absolute bottom-0 h-[2px] w-full bg-border left-0 right-0" />
                <animated.div className="absolute bottom-0 h-[2px] bg-purple-600" style={underlineStyle} />
            </animated.div>
            <div className="grid grid-rows-1 h-full overflow-hidden">
                {items
                    .filter((item) => item !== null)
                    .map((item, index) => (
                        <TabItem item={item} isActive={index === activeItem} key={item?.title} />
                    ))}
            </div>
        </div>
    );
};

export const TabItem: FunctionComponent<{ item: TabItemProps; isActive: boolean }> = ({ item, isActive }) => {
    const resetKey = useRef(0);

    const [style, styleApi] = useSpring(() => ({
        from: {
            opacity: isActive ? 1 : 0,
            pointerEvents: isActive ? 'auto' : 'none',
            zIndex: isActive ? 1 : 0,
            display: isActive ? 'block' : 'none',
        },
    }));

    useEffect(() => {
        styleApi.start(() => ({
            to: {
                opacity: isActive ? 1 : 0,
                pointerEvents: isActive ? 'auto' : 'none',
                zIndex: isActive ? 1 : 0,
                display: 'block',
            },
            onRest: () => {
                styleApi.start({
                    to: {
                        display: isActive ? 'block' : 'none',
                    },
                    immediate: true,
                });
                resetKey.current += 1;
            },
        }));
    }, [isActive, styleApi]);

    return (
        <animated.div
            style={{
                ...(style as SpringValues<{
                    pointerEvents: 'none' | 'auto';
                    position: 'static' | 'absolute';
                    opacity: number;
                    zIndex: number;
                    display: 'block' | 'none';
                }>),
            }}
            data-name="TabItem"
            aria-hidden={!isActive}
            tabIndex={isActive ? 0 : -1}
            className="col-start-1 col-end-1 row-start-1 row-end-1 max-w-full overflow-hidden"
            key={resetKey.current}
        >
            {item.content}
        </animated.div>
    );
};
